import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  inject,
  input,
  signal,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { Auth0Account } from 'src/app/accounts/interfaces/accounts.types';
import { MeService } from 'src/app/me/services/me.service';
import { OnboardingIntentStep } from 'src/app/shared/enums/intent.enum';
import { IntentStrategyService } from 'src/app/shared/services/intent/intent-strategy.service';
import { IntentService } from 'src/app/shared/services/intent/intent.service';
import { openExternalWebPage } from 'src/app/shared/util/link-util';
import { environment } from 'src/environments/environment';
import { Layout, NavigationExtras } from '../../interfaces';
import { LayoutService } from '../../services/layout.service';
import { SideMenuService } from '../../services/side-menu.service';
import { FeatureActivePipe } from '../../../shared/pipes/feature-active.pipe';
import { LokaliseTranslatePipe } from '../../../lokalise/lokalise-translate.pipe';
import { GpuLayoutModule } from '@geneplanet/ngx-ui/src/lib/layout';
import { HeaderLanguageSwitcherComponent } from './header-language-switcher/header-language-switcher.component';
import { AnalyticsModule } from '@geneplanet/ngx-utils';
import { GpsCartCountBadgeModule } from '@geneplanet/ngx-shop/src/lib/cart-count-badge';
import { NgClass } from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    NgClass,
    GpsCartCountBadgeModule,
    RouterLink,
    RouterLinkActive,
    AnalyticsModule,
    HeaderLanguageSwitcherComponent,
    GpuLayoutModule,
    LokaliseTranslatePipe,
    FeatureActivePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnChanges {
  private router = inject(Router);
  private layoutService = inject(LayoutService);
  private meService = inject(MeService);
  private sideMenuService = inject(SideMenuService);
  private intentService = inject(IntentService);
  private intentStrategyService = inject(IntentStrategyService);

  readonly account = input<Auth0Account>(undefined);

  readonly layout = signal<Layout>(undefined);
  readonly extras = signal<NavigationExtras>(undefined);
  readonly simple = signal(false);
  readonly hasSideMenu = signal(false);
  readonly isCollapsable = signal(false);
  readonly isCheckout = signal(false);

  ngOnInit() {
    this.layoutService.layout$
      .pipe(untilDestroyed(this))
      .subscribe((layout: Layout) => {
        this.layout.set(layout);
        this.updateState();
      });
    this.layoutService.navigationExtras$
      .pipe(untilDestroyed(this))
      .subscribe((extras: NavigationExtras) => {
        this.extras.set(extras);
        this.updateState();
      });
    this.sideMenuService.isCollapsable$
      .pipe(untilDestroyed(this))
      .subscribe((collapsable) => this.isCollapsable.set(collapsable));
  }

  ngOnChanges() {
    this.updateState();
  }

  redirectToWebsiteOrDashboard() {
    // when user is onboarding, click in logo should take him to website (open in new tab)
    if (
      this.router.url.startsWith('/accounts') ||
      this.router.url.startsWith('/onboarding') ||
      this.router.url.startsWith('/nipt/nipt-expired')
    ) {
      openExternalWebPage(environment.pages.home);
    } else {
      this.onCancelIntentClick();
    }
  }

  openSideMenu() {
    this.sideMenuService.openToggle();
  }

  logout(): void {
    this.meService.logout();
    this.router.navigate(['accounts/logged-out']);
  }

  isRouterLinkActive(url: string): boolean {
    return this.router.isActive(url, false);
  }

  onNavItemClicked() {
    this.sideMenuService.setDefault();
  }

  onSkipClick() {
    if (
      this.layout()?.header?.skipLink?.step ===
      OnboardingIntentStep.DownloadAfterCheck
    ) {
      this.intentStrategyService.context.onDownloadAfterCheckSkipClick();
    } else {
      this.intentStrategyService.context.onTrackSkipClick();
    }
  }

  onClose() {
    if (this.layout()?.header?.cancelIntent) {
      this.onCancelIntentClick();
    } else if (this.layout()?.header?.closeButton) {
      this.layoutService.headerCloseClick();
    }
  }

  private onCancelIntentClick() {
    this.intentService
      .cancelIntent()
      .pipe(
        finalize(() => {
          this.router.navigate(['/dashboard#overview']);
        })
      )
      .subscribe();
  }

  private updateState() {
    this.isCheckout.set(this.layout()?.footer?.bottomOffset === 'checkout');
    this.simple.set(!this.account() || this.layout()?.header?.simple);
    this.hasSideMenu.set(this.account() && !this.layout()?.sideMenu?.hide);
    this.layoutService.headerResized$.next();
  }
}
